"use client";

import { AppUserSession } from "@packages/lib/auth/types"; // adjust import if needed
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";

type RouteChangeTrackerProps = {
  session: Partial<AppUserSession> | null | undefined;
};

// Define a type for the data layer event payload
type ElevarDataLayerEvent = Record<string, unknown>;

// Type for extended window object
interface ExtendedWindow extends Window {
  ElevarInvalidateContext?: () => void;
  ElevarDataLayer?: ElevarDataLayerEvent[];
}

// Ensure ElevarDataLayer is initialized
const ensureElevarDataLayer = () => {
  if (typeof window !== "undefined") {
    (window as ExtendedWindow).ElevarDataLayer =
      (window as ExtendedWindow).ElevarDataLayer || [];
  }
};

export function RouteChangeTracker({ session }: RouteChangeTrackerProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // stable query string for deps & readability
  const queryString = searchParams?.toString?.() ?? "";

  // keep access to just the pieces we depend on to avoid unnecessary re-runs
  const user = session?.user;
  const isDev = Boolean(session?.userFlags?.isDev);

  // Initialize ElevarDataLayer early
  ensureElevarDataLayer();

  useEffect(() => {
    const w = window as ExtendedWindow;

    // Ensure ElevarDataLayer exists
    w.ElevarDataLayer = w.ElevarDataLayer || [];

    // reset Elevar context if available
    if (typeof w.ElevarInvalidateContext === "function") {
      w.ElevarInvalidateContext();
    }

    // Elevar expects a visitor_type; currently this site requires login,
    // so keep "logged_in" explicit. Change if guest flows are introduced.
    const userProperties: Record<string, unknown> = {
      visitor_type: "logged_in",
    };

    // source values: keep numbers as numbers and strings as strings here,
    // coerce to strings only when populating the final userProperties
    const possibleFields: Record<string, unknown> = {
      customer_address_1: user?.address_1,
      customer_address_2: user?.address_2,
      customer_city: user?.city,
      customer_country: user?.country,
      customer_email: user?.email,
      customer_first_name: user?.first_name,
      customer_id: user?.id,
      customer_last_name: user?.last_name,
      customer_nickname: user?.nickname,
      customer_order_count: user?.order_count,
      customer_phone: user?.phone,
      customer_province: user?.province,
      customer_province_code: user?.province_code,
      customer_tags: user?.tags,
      customer_total_spent: user?.total_spent,
      customer_zip: user?.zip,
    };

    Object.entries(possibleFields).forEach(([key, value]) => {
      if (value == null) return;

      if (typeof value === "string") {
        const v = value.trim();
        if (v.length > 0) userProperties[key] = v;
      } else if (typeof value === "number" && !isNaN(value)) {
        // Elevar requires strings for some numeric fields; convert explicitly here
        userProperties[key] = String(value);
      } else if (typeof value === "object") {
        // defensively stringify objects (rare for our claims, but safe)
        try {
          const s = JSON.stringify(value);
          if (s && s !== "{}") userProperties[key] = s;
        } catch {
          // ignore non-serializable objects
        }
      }
    });

    const payload: ElevarDataLayerEvent = {
      event: "dl_user_data",
      cart_total: "0.00",
      user_properties: userProperties,
      ecommerce: {
        currencyCode: "USD",
        cart_contents: {
          products: [],
        },
      },
      page_path: pathname + (queryString ? "?" + queryString : ""),
      page_location: typeof window !== "undefined" ? window.location.href : "",
      page_title: typeof document !== "undefined" ? document.title : "",
    };

    // small readiness + retry helper: try a few times if ElevarDataLayer isn't ready yet
    const timers: number[] = [];
    let attempts = 0;
    const maxAttempts = 5;
    const tryPush = () => {
      attempts++;
      if (w.ElevarDataLayer && Array.isArray(w.ElevarDataLayer)) {
        try {
          w.ElevarDataLayer.push(payload);
          if (isDev) console.debug("Elevar payload pushed", payload);
        } catch (err) {
          if (isDev) console.warn("Elevar push failed", err);
        }
      } else if (attempts < maxAttempts) {
        // exponential-ish backoff
        const t = window.setTimeout(tryPush, 150 * attempts);
        timers.push(t);
      } else {
        if (isDev) console.warn("ElevarDataLayer not available after retries");
      }
    };

    tryPush();

    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, [pathname, queryString, user, isDev]);

  return null;
}
